import React from "react";
import styled from "styled-components";
import CancelIcon from "@material-ui/icons/Cancel";
import { RouteComponentProps, withRouter } from "react-router";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { Avatar } from "@sonder/ui/source/styled/components/Avatar";
import { PlainButton } from "@sonder/ui/source/styled/fragments/PlainButton";
import { Cell } from "@sonder/ui/source/styled/fragments/Table";
import { colors } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import {
  BuildingsData,
  GetBuildingSupervisorsCleaningSupervisors,
} from "core/queries/GetBuildingSupervisors";
import { useUpdateBuildingSupervisors } from "core/queries/UpdateBuildingSupervisors";

interface SupervisorRowProps extends RouteComponentProps {
  building: BuildingsData;
  lastRow?: boolean;
  loading?: boolean;
  supervisor: GetBuildingSupervisorsCleaningSupervisors;
}

const StyledActionCell = styled(Cell)`
  justify-content: flex-end;
`;

const StyledUserCardCell = styled(Cell)`
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
`;

const StyledEmail = styled.div`
  color: ${colors.gray6};
  font-size: 0.8rem;
`;

export const Row = withRouter(
  ({ building, lastRow, loading, supervisor }: SupervisorRowProps) => {
    const { state: authState } = useAuthStore();
    const { hovered, onMouseEnter, onMouseLeave } = useHovered();
    const errorReporter = useErrorReporterFiltered();
    const [onUpdateBuildingSupervisors, { loading: isUpdating }] =
      useUpdateBuildingSupervisors({
        awaitRefetchQueries: true,
        refetchQueries: ["GetBuildingSupervisors"],
      });

    const { id, avatar, email, name } = supervisor;

    const isDisabled = loading || isUpdating;
    const sharedCellProps = {
      disabled: isDisabled,
      rowKey: id,
      onMouseEnter,
      onMouseLeave,
    };

    return (
      <>
        <Cell {...sharedCellProps} rowStart lastRow={lastRow}>
          <Avatar imageUrl={avatar?.url} name={name} />
        </Cell>
        <StyledUserCardCell {...sharedCellProps} lastRow={lastRow}>
          <div>{name}</div>
          <StyledEmail>{email}</StyledEmail>
        </StyledUserCardCell>
        <StyledActionCell {...sharedCellProps} rowEnd lastRow={lastRow}>
          {authState.user?.isDispatchDashboardAdmin && hovered && (
            <PlainButton
              disabled={isDisabled}
              onClick={() => {
                onUpdateBuildingSupervisors({
                  variables: {
                    buildingId: building.id,
                    removedSupervisors: [supervisor.id],
                  },
                }).catch(error => {
                  errorReporter.notify(error);
                });
              }}
            >
              <CancelIcon />
            </PlainButton>
          )}
        </StyledActionCell>
      </>
    );
  },
);
